import React, { useState, useEffect } from 'react';
import './AgendasMinutesPage.css';

const API_BASE = '';

function AgendasMinutesPage({ isAdmin, authToken }) {
  const [agendas, setAgendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showMassUpload, setShowMassUpload] = useState(false);
  const [massUploadFiles, setMassUploadFiles] = useState([]);
  const [massUploadProgress, setMassUploadProgress] = useState(null);
  const [showFormatInfo, setShowFormatInfo] = useState(false);
  const [selectedAdditionalDoc, setSelectedAdditionalDoc] = useState({});
  const [newAgenda, setNewAgenda] = useState({
    date: '',
    agenda_pdf: null,
    agenda_packet_pdf: null,
    meeting_minutes_pdf: null,
    additional_documents: [],
    notes: ''
  });

  useEffect(() => {
    loadAgendas();
  }, []);

  const loadAgendas = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/events`);
      const data = await response.json();
      const sortedAgendas = data
        .filter(item => item.type === 'agenda')
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setAgendas(sortedAgendas);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load agendas:', error);
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, field, agendaId = null) => {
    const formData = new FormData();
    formData.append('file', file);

    // If we have an agendaId, get the agenda date to organize files by date
    if (agendaId) {
      const agenda = agendas.find(a => a.id === agendaId);
      if (agenda && agenda.date) {
        formData.append('agenda_date', agenda.date);
      }
    }

    try {
      const response = await fetch(`${API_BASE}/api/admin/upload-document`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (agendaId) {
          // Update existing agenda
          // For single-file fields (pdf_filename, agenda_packet_pdf, meeting_minutes_pdf), just use filename
          // For additional_documents, caller will handle the full object
          if (field === 'additional_document') {
            return data; // Return full object with filename and display_name
          } else {
            await updateAgendaField(agendaId, field, data.filename);
            return data.filename;
          }
        } else {
          // For new agenda form
          setNewAgenda(prev => ({
            ...prev,
            [field]: data.filename
          }));
          return data.filename;
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file');
    }
    return null;
  };

  const updateAgendaField = async (agendaId, field, value) => {
    try {
      const agenda = agendas.find(a => a.id === agendaId);
      const updatedAgenda = { ...agenda, [field]: value };
      
      // Backend expects form data, not JSON
      const formData = new FormData();
      formData.append('type', updatedAgenda.type || 'agenda');
      formData.append('date', updatedAgenda.date);
      formData.append('time', updatedAgenda.time || '7:00 PM');
      formData.append('title', updatedAgenda.title || `Agenda: ${formatDate(updatedAgenda.date)}`);
      formData.append('description', updatedAgenda.description || updatedAgenda.notes || '');
      
      // ALWAYS send ALL document fields to preserve them
      formData.append('pdf_filename', updatedAgenda.pdf_filename || updatedAgenda.agenda_pdf || '');
      formData.append('agenda_packet_pdf', updatedAgenda.agenda_packet_pdf || '');
      formData.append('meeting_minutes_pdf', updatedAgenda.meeting_minutes_pdf || '');
      const additionalDocsToSend = JSON.stringify(updatedAgenda.additional_documents || []);
      console.log('Sending additional_documents:', additionalDocsToSend);
      formData.append('additional_documents', additionalDocsToSend);
      
      console.log('Updating field:', field, 'with value:', value);
      console.log('Preserving agenda_packet_pdf:', updatedAgenda.agenda_packet_pdf);
      console.log('Preserving meeting_minutes_pdf:', updatedAgenda.meeting_minutes_pdf);
      
      const response = await fetch(`${API_BASE}/api/admin/events/${agendaId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: formData
      });
      
      if (response.ok) {
        console.log('Update successful');
        loadAgendas();
      } else {
        const error = await response.text();
        console.error('Update failed:', response.status, error);
        alert(`Failed to update: ${error}`);
      }
    } catch (error) {
      console.error('Update failed:', error);
      alert('Failed to update document');
    }
  };

  const handleAddAgenda = async () => {
    if (!newAgenda.date) {
      alert('Please select a date');
      return;
    }

    try {
      // Backend expects form data, not JSON
      const formData = new FormData();
      formData.append('type', 'agenda');
      formData.append('title', `Agenda: ${formatDate(newAgenda.date)}`);
      formData.append('date', newAgenda.date);
      formData.append('time', '7:00 PM');
      formData.append('description', newAgenda.notes || '');
      
      const response = await fetch(`${API_BASE}/api/admin/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: formData
      });

      if (response.ok) {
        setShowAddForm(false);
        setNewAgenda({
          date: '',
          agenda_pdf: null,
          agenda_packet_pdf: null,
          meeting_minutes_pdf: null,
          additional_documents: [],
          notes: ''
        });
        loadAgendas();
      } else {
        const errorText = await response.text();
        console.error('Failed to create event:', response.status, errorText);
        alert(`Failed to create meeting: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Failed to add agenda:', error);
      alert('Failed to add agenda');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this agenda and all its documents?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/admin/events/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${authToken}`
        }
      });

      if (response.ok) {
        loadAgendas();
      }
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete agenda');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const removeDocument = async (agendaId, field) => {
    if (!window.confirm(`Remove this document?`)) return;
    await updateAgendaField(agendaId, field, null);
  };

  const removeAdditionalDoc = async (agendaId, docIndex) => {
    if (!window.confirm(`Remove this document?`)) return;
    const agenda = agendas.find(a => a.id === agendaId);
    const updatedDocs = [...(agenda.additional_documents || [])];
    console.log('Before removal:', updatedDocs);
    updatedDocs.splice(docIndex, 1);
    console.log('After removal:', updatedDocs);
    await updateAgendaField(agendaId, 'additional_documents', updatedDocs);
  };

  const addAdditionalDocuments = async (agendaId, newDocObjects) => {
    const agenda = agendas.find(a => a.id === agendaId);
    const currentDocs = agenda.additional_documents || [];
    const updatedDocs = [...currentDocs, ...newDocObjects];
    await updateAgendaField(agendaId, 'additional_documents', updatedDocs);
  };

  const handleMassUpload = async () => {
    if (!massUploadFiles || massUploadFiles.length === 0) {
      alert('Please select a ZIP file to upload');
      return;
    }

    const file = massUploadFiles[0];
    if (!file.name.endsWith('.zip')) {
      alert('Please select a ZIP file');
      return;
    }

    let progressMessages = [];
    const addProgressMessage = (msg) => {
      progressMessages.push(msg);
      setMassUploadProgress({ 
        status: 'uploading', 
        message: progressMessages.join('\n'),
        messages: progressMessages
      });
    };

    const formData = new FormData();
    formData.append('file', file);

    // Set up SSE for real-time progress (pass auth in URL since EventSource doesn't support headers)
    const encodedAuth = encodeURIComponent(authToken);
    const eventSource = new EventSource(`${API_BASE}/api/admin/upload-progress?auth=${encodedAuth}`);

    eventSource.addEventListener('progress', (event) => {
      const data = JSON.parse(event.data);
      addProgressMessage(data.message);
    });

    eventSource.addEventListener('done', () => {
      eventSource.close();
    });

    eventSource.addEventListener('error', () => {
      eventSource.close();
    });

    try {
      const response = await fetch(`${API_BASE}/api/admin/mass-upload-agendas`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: formData
      });

      // SSE will close automatically

      if (response.ok) {
        const result = await response.json();
        
        // Build detailed progress message
        let message = `✅ Successfully processed ${file.name}!\n\n`;
        message += `📄 Created: ${result.created} new events\n`;
        message += `🔄 Updated: ${result.updated} existing events\n`;
        
        // Show processed files by year
        if (result.details && result.details.all_processed) {
          const byYear = {};
          result.details.all_processed.forEach(item => {
            const year = item.date.substring(0, 4);
            if (!byYear[year]) byYear[year] = 0;
            byYear[year]++;
          });
          
          if (Object.keys(byYear).length > 0) {
            message += `\n📅 Documents by year:\n`;
            Object.keys(byYear).sort().forEach(year => {
              message += `  • ${year}: ${byYear[year]} documents\n`;
            });
          }
        }
        
        if (result.errors > 0) {
          message += `\n⚠️ ${result.errors} files couldn't be processed`;
        }
        
        setMassUploadProgress({
          status: 'success',
          message: message,
          details: result.details
        });
        
        loadAgendas();
        
        // Don't auto-hide - let user dismiss or navigate away
        // User can refresh page or click away when they're done reviewing
      } else {
        const error = await response.text();
        setMassUploadProgress({
          status: 'error',
          message: `❌ Upload failed: ${error}`
        });
      }
    } catch (error) {
      console.error('Mass upload failed:', error);
      setMassUploadProgress({
        status: 'error',
        message: 'Upload failed: ' + error.message
      });
    }
  };

  return (
    <div className="agendas-minutes-page">
      <div className="page-header">
        <h1>Agendas & Minutes</h1>
        {isAdmin && (
          <div className="admin-buttons">
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? 'Cancel' : '+ Add Meeting'}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowMassUpload(!showMassUpload)}
            >
              📦 Mass Upload ZIP
            </button>
            <button
              className="btn-info-small"
              onClick={() => setShowFormatInfo(!showFormatInfo)}
              title="Click for format requirements"
              style={{display: showMassUpload ? 'inline-flex' : 'none'}}
            >
              ℹ
            </button>
          </div>
        )}
      </div>

      {isAdmin && showMassUpload && (
        <div className="mass-upload-form">
          <h3>Mass Upload Agendas & Minutes</h3>
          {showFormatInfo && (
            <div className="format-info-modal">
              <div className="format-info-content">
                <button className="close-btn" onClick={() => setShowFormatInfo(false)}>×</button>
                <h4>📋 Flexible Format Requirements</h4>
                <div className="format-section">
                  <h5>📁 Folder Categories (put files in ANY of these):</h5>
                  <ul>
                    <li><strong>Agendas:</strong> "agendas" folder</li>
                    <li><strong>Minutes:</strong> "minutes" folder</li>
                    <li><strong>Agenda Packets:</strong> "packets" folder</li>
                    <li><strong>Additional Documents:</strong> "additional", "extra", "other", or "misc" folder</li>
                  </ul>
                </div>
                <div className="format-section">
                  <h5>📄 File Naming (dates can be in any format):</h5>
                  <ul>
                    <li><strong>Just include a date somewhere in the filename</strong></li>
                    <li><strong>Examples:</strong> 1.22.2024.pdf, 2024-01-22_agenda.pdf, 01_22_24_minutes.docx</li>
                  </ul>
                </div>
                <div className="format-section">
                  <h5>✅ Example ZIP Structure:</h5>
                  <code className="example-block">
📁 agendas/<br/>
&nbsp;&nbsp;📄 1.22.2024.pdf<br/>
&nbsp;&nbsp;📄 2-15-24_meeting.pdf<br/>
📁 minutes/<br/>
&nbsp;&nbsp;📄 2024_01_15.pdf<br/>
&nbsp;&nbsp;📄 03.10.24_board.docx<br/>
📁 packets/<br/>
&nbsp;&nbsp;📄 01-22-2024_packet.pdf<br/>
📁 additional/<br/>
&nbsp;&nbsp;📄 2024.01.15_notice.pdf
                  </code>
                </div>
              </div>
            </div>
          )}
          <div className="upload-instructions">
            <p><strong>Quick Instructions:</strong></p>
            <ul>
              <li>Upload a ZIP file with year folders containing agenda/minutes subfolders</li>
              <li>Files must start with a date in various formats</li>
              <li>Click the ℹ button above for detailed format requirements</li>
            </ul>
          </div>
          <div className="upload-area">
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setMassUploadFiles(Array.from(e.target.files))}
              id="mass-upload-input"
            />
            <label htmlFor="mass-upload-input" className="upload-label">
              {massUploadFiles.length > 0 ? 
                `Selected: ${massUploadFiles[0].name}` : 
                'Click to select ZIP file'
              }
            </label>
          </div>
          {massUploadProgress && (
            <div className={`upload-progress ${massUploadProgress.status}`}>
              {massUploadProgress.status === 'success' && (
                <button 
                  onClick={() => {
                    setMassUploadProgress(null);
                    setMassUploadFiles([]);
                  }}
                  style={{
                    float: 'right',
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '0 5px'
                  }}
                  title="Close"
                >
                  ✖
                </button>
              )}
              <div style={{
                maxHeight: '500px',
                overflowY: 'auto',
                backgroundColor: '#f0f0f0',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap'
              }}>
                {massUploadProgress.messages ? 
                  massUploadProgress.messages.map((msg, idx) => (
                    <div key={idx} style={{
                      color: msg.includes('❌') || msg.includes('Failed') ? 'red' : 
                             msg.includes('✅') ? 'green' : 'black'
                    }}>
                      {msg}
                    </div>
                  ))
                  : massUploadProgress.message
                }
              </div>
              {massUploadProgress.details && massUploadProgress.details.errors && massUploadProgress.details.errors.length > 0 && (
                <details style={{marginTop: '10px'}}>
                  <summary>Error Details</summary>
                  <ul>
                    {massUploadProgress.details.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          <div className="form-actions">
            <button 
              className="btn btn-success" 
              onClick={handleMassUpload}
              disabled={massUploadFiles.length === 0 || massUploadProgress?.status === 'uploading'}
            >
              Upload and Process
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowMassUpload(false);
                setMassUploadFiles([]);
                setMassUploadProgress(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {isAdmin && showAddForm && (
        <div className="add-agenda-form">
          <h3>Add New Meeting</h3>
          <div className="form-row">
            <label>
              Meeting Date:
              <input
                type="date"
                value={newAgenda.date}
                onChange={(e) => setNewAgenda({...newAgenda, date: e.target.value})}
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Notes:
              <textarea
                value={newAgenda.notes}
                onChange={(e) => setNewAgenda({...newAgenda, notes: e.target.value})}
                placeholder="Optional notes about this meeting"
              />
            </label>
          </div>
          <div className="form-actions">
            <button className="btn btn-success" onClick={handleAddAgenda}>
              Create Meeting Entry
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading agendas...</div>
      ) : (
        <div className="agendas-table-container">
          <table className="agendas-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Agenda</th>
                <th>Agenda Packet</th>
                <th>Meeting Minutes</th>
                <th>Additional Documents</th>
                <th>Notes</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {agendas.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} className="no-data">
                    No meetings found
                  </td>
                </tr>
              ) : (
                agendas.map(agenda => (
                  <tr key={agenda.id}>
                    <td className="date-cell">
                      {formatDate(agenda.date)}
                    </td>
                    <td className="document-cell">
                      {agenda.pdf_filename || agenda.agenda_pdf ? (
                        isAdmin ? (
                          <div className="document-actions">
                            <label className="btn-icon btn-replace" title="Replace">
                              ↻
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleFileUpload(e.target.files[0], 'pdf_filename', agenda.id);
                                  }
                                }}
                              />
                            </label>
                            <button 
                              className="btn-icon btn-remove"
                              onClick={() => removeDocument(agenda.id, 'pdf_filename')}
                              title="Remove document"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <a 
                            href={agenda.pdf_url || `/pdfs/${agenda.pdf_filename || agenda.agenda_pdf}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="document-link-simple"
                          >
                            📄 View
                          </a>
                        )
                      ) : isAdmin ? (
                        <label className="btn-icon btn-upload" title="Upload">
                          +
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleFileUpload(e.target.files[0], 'pdf_filename', agenda.id);
                              }
                            }}
                          />
                        </label>
                      ) : (
                        <span className="no-document">-</span>
                      )}
                    </td>
                    <td className="document-cell">
                      {agenda.agenda_packet_pdf ? (
                        isAdmin ? (
                          <div className="document-actions">
                            <label className="btn-icon btn-replace" title="Replace">
                              ↻
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleFileUpload(e.target.files[0], 'agenda_packet_pdf', agenda.id);
                                  }
                                }}
                              />
                            </label>
                            <button 
                              className="btn-icon btn-remove"
                              onClick={() => removeDocument(agenda.id, 'agenda_packet_pdf')}
                              title="Remove document"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <a 
                            href={`/pdfs/${agenda.agenda_packet_pdf}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="document-link-simple"
                          >
                            📄 View
                          </a>
                        )
                      ) : isAdmin ? (
                        <label className="btn-icon btn-upload" title="Upload">
                          +
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleFileUpload(e.target.files[0], 'agenda_packet_pdf', agenda.id);
                              }
                            }}
                          />
                        </label>
                      ) : (
                        <span className="no-document">-</span>
                      )}
                    </td>
                    <td className="document-cell">
                      {agenda.meeting_minutes_pdf ? (
                        isAdmin ? (
                          <div className="document-actions">
                            <label className="btn-icon btn-replace" title="Replace">
                              ↻
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleFileUpload(e.target.files[0], 'meeting_minutes_pdf', agenda.id);
                                  }
                                }}
                              />
                            </label>
                            <button 
                              className="btn-icon btn-remove"
                              onClick={() => removeDocument(agenda.id, 'meeting_minutes_pdf')}
                              title="Remove document"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <a 
                            href={`/pdfs/${agenda.meeting_minutes_pdf}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="document-link-simple"
                          >
                            📄 View
                          </a>
                        )
                      ) : isAdmin ? (
                        <label className="btn-icon btn-upload" title="Upload">
                          +
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleFileUpload(e.target.files[0], 'meeting_minutes_pdf', agenda.id);
                              }
                            }}
                          />
                        </label>
                      ) : (
                        <span className="no-document">-</span>
                      )}
                    </td>
                    <td className="document-cell">
                      <div className="additional-docs-dropdown">
                        {isAdmin ? (
                          <>
                            <label className="btn-icon btn-add" title="Add file">
                              +
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={async (e) => {
                                  if (e.target.files[0]) {
                                    const result = await handleFileUpload(e.target.files[0], 'additional_document', agenda.id);
                                    if (result) {
                                      // result is { filename, display_name, url }
                                      await addAdditionalDocuments(agenda.id, [result]);
                                      setSelectedAdditionalDoc({
                                        ...selectedAdditionalDoc,
                                        [agenda.id]: agenda.additional_documents ? agenda.additional_documents.length : 0
                                      });
                                    }
                                  }
                                }}
                              />
                            </label>
                            <select 
                              className="additional-docs-select"
                              value={selectedAdditionalDoc[agenda.id] || ''}
                              onChange={(e) => {
                                setSelectedAdditionalDoc({
                                  ...selectedAdditionalDoc,
                                  [agenda.id]: e.target.value
                                });
                              }}
                            >
                              <option value="">
                                {agenda.additional_documents && agenda.additional_documents.length > 0
                                  ? `${agenda.additional_documents.length} file(s)`
                                  : 'No addtl files'}
                              </option>
                              {agenda.additional_documents && agenda.additional_documents.map((doc, idx) => {
                                // doc is an object with { filename, display_name }
                                const displayName = doc.display_name || doc.filename || doc;
                                return (
                                  <option key={idx} value={idx}>
                                    {displayName.length > 40 ? displayName.substring(0, 37) + '...' : displayName}
                                  </option>
                                );
                              })}
                            </select>
                            {agenda.additional_documents && agenda.additional_documents.length > 0 && (
                              <button 
                                className="btn-icon btn-remove"
                                onClick={() => {
                                  const idx = parseInt(selectedAdditionalDoc[agenda.id]);
                                  if (!isNaN(idx) && idx >= 0) {
                                    removeAdditionalDoc(agenda.id, idx);
                                    setSelectedAdditionalDoc({
                                      ...selectedAdditionalDoc,
                                      [agenda.id]: ''
                                    });
                                  }
                                }}
                                title="Delete selected"
                              >
                                ×
                              </button>
                            )}
                          </>
                        ) : (
                          agenda.additional_documents && agenda.additional_documents.length > 0 ? (
                            <select
                              className="additional-docs-select"
                              onChange={(e) => {
                                if (e.target.value) {
                                  const doc = agenda.additional_documents[e.target.value];
                                  const filename = doc.filename || doc;
                                  window.open(`/pdfs/${filename}`, '_blank');
                                }
                              }}
                            >
                              <option value="">View docs ({agenda.additional_documents.length})</option>
                              {agenda.additional_documents.map((doc, idx) => {
                                const displayName = doc.display_name || doc.filename || doc;
                                return (
                                  <option key={idx} value={idx}>
                                    {displayName}
                                  </option>
                                );
                              })}
                            </select>
                          ) : (
                            <span className="no-document">-</span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="notes-cell">
                      {editingId === agenda.id && isAdmin ? (
                        <textarea
                          defaultValue={agenda.description || ''}
                          autoFocus
                          onBlur={(e) => {
                            updateAgendaField(agenda.id, 'description', e.target.value);
                            setEditingId(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.ctrlKey) {
                              updateAgendaField(agenda.id, 'description', e.target.value);
                              setEditingId(null);
                            }
                          }}
                        />
                      ) : (
                        <div 
                          className={isAdmin ? 'editable-notes' : ''}
                          onClick={() => isAdmin && setEditingId(agenda.id)}
                          title={isAdmin ? "Click to edit" : ""}
                        >
                          {agenda.description || <span className="no-notes">{isAdmin ? "Click to add notes" : "-"}</span>}
                        </div>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="actions-cell">
                        <button 
                          className="btn-icon btn-remove"
                          onClick={() => handleDelete(agenda.id)}
                          title="Delete"
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AgendasMinutesPage;